/**
 * CoordinationModePanel - Panel for RF Coordination Analysis
 * Displays real-time analysis, markers, and frequency recommendations
 */

import { useState } from "react";
import { 
  Play, 
  Square, 
  Download, 
  Clock, 
  Activity, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Radio,
  Zap,
  BarChart3,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useUSBConnection } from "@/contexts/USBConnectionContext";

export default function CoordinationModePanel({ onExport }) {
  const {
    coordinationMode,
    coordinationData,
    startCoordinationMode,
    stopCoordinationMode,
    resetCoordinationData,
    serialScanning,
  } = useUSBConnection();
  
  const [showDetails, setShowDetails] = useState(false);
  
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getCurrentDuration = () => {
    if (!coordinationData.startTime) return 0;
    return (new Date() - coordinationData.startTime) / 1000;
  };
  
  const handleStop = () => {
    const analysis = stopCoordinationMode();
    if (analysis && onExport) {
      // Optionally auto-show export dialog
    }
  };
  
  const handleExport = () => {
    const analysis = coordinationData.analysis;
    if (!analysis) return;
    
    // Generate report
    const report = {
      title: "AVX RF PRO - Análisis de Coordinación",
      generatedAt: new Date().toISOString(),
      duration: `${formatDuration(analysis.duration)}`,
      totalScans: analysis.totalScans,
      summary: analysis.summary,
      recommendedRanges: analysis.recommendedRanges,
      cleanFrequencies: analysis.cleanFrequencies,
      occupiedFrequencies: analysis.occupiedFrequencies,
      unstableFrequencies: analysis.unstableFrequencies,
      markers: analysis.markers,
    };
    
    // Download as JSON
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `avx-coordination-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Not scanning - show start button
  if (!serialScanning && !coordinationMode && !coordinationData.analysis) {
    return (
      <div className="bg-[#121214] border border-[#27272A] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[#27272A] flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#71717A]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Modo Coordinación</h3>
            <p className="text-xs text-[#71717A]">Inicia el escaneo USB para habilitar</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Analysis complete - show results
  if (coordinationData.analysis) {
    const analysis = coordinationData.analysis;
    
    return (
      <div className="bg-[#121214] border border-[#27272A] rounded-xl p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#34C759]/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Análisis Completado</h3>
              <p className="text-xs text-[#71717A]">
                {formatDuration(analysis.duration)} • {analysis.totalScans} escaneos
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
            >
              <Download className="w-4 h-4 mr-1" />
              Exportar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={resetCoordinationData}
              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
            >
              Nuevo
            </Button>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-[#34C759] font-mono">
              {analysis.summary.cleanCount}
            </div>
            <div className="text-xs text-[#34C759]/80">Limpias</div>
          </div>
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-[#FF3B30] font-mono">
              {analysis.summary.occupiedCount}
            </div>
            <div className="text-xs text-[#FF3B30]/80">Ocupadas</div>
          </div>
          <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-[#FFCC00] font-mono">
              {analysis.summary.unstableCount}
            </div>
            <div className="text-xs text-[#FFCC00]/80">Inestables</div>
          </div>
        </div>
        
        {/* Recommended Ranges */}
        {analysis.recommendedRanges.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-2">
              Rangos Recomendados
            </h4>
            <div className="space-y-2">
              {analysis.recommendedRanges.slice(0, 3).map((range, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between bg-[#18181B] rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Radio className="w-4 h-4 text-[#34C759]" />
                    <span className="text-sm font-mono text-white">
                      {range.start.toFixed(1)} - {range.end.toFixed(1)} MHz
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs border-[#34C759]/50 text-[#34C759]">
                      {range.width.toFixed(1)} MHz
                    </Badge>
                    <span className="text-xs text-[#71717A]">
                      {range.avgNoise.toFixed(0)} dBm
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Event Markers */}
        {analysis.markers.length > 0 && (
          <div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-xs text-[#71717A] hover:text-white transition-colors"
            >
              <AlertTriangle className="w-3 h-3" />
              {analysis.markers.length} eventos detectados
              <span className="text-[#52525B]">{showDetails ? '▲' : '▼'}</span>
            </button>
            
            {showDetails && (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                {analysis.markers.slice(-10).reverse().map((marker, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 text-xs bg-[#18181B] rounded px-2 py-1"
                  >
                    <Zap className="w-3 h-3 text-[#FFCC00]" />
                    <span className="text-[#71717A]">
                      {new Date(marker.time).toLocaleTimeString()}
                    </span>
                    <span className="text-white">{marker.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  
  // Coordination mode active - show progress
  if (coordinationMode) {
    const duration = getCurrentDuration();
    const scanCount = coordinationData.scanCount;
    const markerCount = coordinationData.markers.length;
    const freqCount = Object.keys(coordinationData.frequencyStats).length;
    
    return (
      <div className="bg-[#121214] border border-[#34C759]/50 rounded-xl p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#34C759]/20 flex items-center justify-center relative">
              <Activity className="w-5 h-5 text-[#34C759]" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#34C759] rounded-full animate-pulse" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#34C759]">Analizando...</h3>
              <p className="text-xs text-[#71717A]">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatDuration(duration)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleStop}
            className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white"
          >
            <Square className="w-4 h-4 mr-1" />
            Detener
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-lg font-bold text-white font-mono">{scanCount}</div>
            <div className="text-xs text-[#71717A]">Escaneos</div>
          </div>
          <div>
            <div className="text-lg font-bold text-white font-mono">{freqCount}</div>
            <div className="text-xs text-[#71717A]">Frecuencias</div>
          </div>
          <div>
            <div className="text-lg font-bold text-[#FFCC00] font-mono">{markerCount}</div>
            <div className="text-xs text-[#71717A]">Eventos</div>
          </div>
        </div>
        
        {/* Progress hint */}
        <div className="text-xs text-center text-[#52525B]">
          Recomendado: mínimo 2 minutos para mejor análisis
        </div>
      </div>
    );
  }
  
  // Ready to start coordination
  return (
    <div className="bg-[#121214] border border-[#27272A] rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#27272A] flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Modo Coordinación</h3>
            <p className="text-xs text-[#71717A]">Analiza el espectro para encontrar frecuencias limpias</p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={startCoordinationMode}
          disabled={!serialScanning}
          className="bg-[#34C759] hover:bg-[#34C759]/80 text-black"
        >
          <Play className="w-4 h-4 mr-1" />
          Iniciar
        </Button>
      </div>
    </div>
  );
}
